/**
 * MessageFileAttachments 组件
 * 用于渲染消息中的文件附件（图片和文档）
 */
import React from 'react';
import { MessageContent, ImageBlock, DocumentBlock } from '../../../../types';
import { IOSFilePreviewItem } from '../chatInput/IOSFilePreviewItem';

interface MessageFileAttachmentsProps {
  content: MessageContent[];
}

/**
 * 将图片 URL 转换为缩略图 URL
 */
const convertToThumbnailUrl = (url: string | undefined): string => {
  if (!url || typeof url !== 'string') return '';
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) return url;

  const baseName = url.substring(0, lastDotIndex);
  const extension = url.substring(lastDotIndex);
  return `${baseName}.thumb${extension}`;
};

/**
 * 从文件名获取文件扩展名
 */
const getFileExtension = (url: string | undefined): string => {
  if (!url || typeof url !== 'string') return '';
  const lastDotIndex = url.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return url.substring(lastDotIndex + 1).toLowerCase();
};

/**
 * 渲染文件附件组件
 */
export const MessageFileAttachments: React.FC<MessageFileAttachmentsProps> = ({ content }) => {
  const fileBlocks = content.filter((item): item is ImageBlock | DocumentBlock =>
    item.type === 'image' || item.type === 'document'
  );

  if (fileBlocks.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-2 pb-2">
      {fileBlocks.map((block, index) => {
        let url: string | undefined = undefined;
        let displayUrl: string | undefined = undefined;
        let fileName: string = '';
        let fileType: string = 'text/plain';
        const isImage = block.type === 'image';
        const isPlainText = block.type === 'document' && block.source.type === 'text';

        if (!isPlainText) {
          const blockSource = block.source as any;
          url = blockSource?.url;

          if (url) {
            displayUrl = isImage ? convertToThumbnailUrl(url) : url;
            const extension = getFileExtension(url);
            const urlParts = url.split('/');
            fileName = urlParts[urlParts.length - 1] || `file.${extension}`;
            fileType = isImage ? `image/${extension}` : `application/${extension}`;
          } else {
            fileName = 'unknown_file';
            fileType = isImage ? 'image/unknown' : 'application/unknown';
          }
        }

        return (
          <IOSFilePreviewItem
            key={index}
            messageMode={{
              url: displayUrl,
              originalUrl: url,
              fileName: fileName,
              fileType: fileType,
              isImage: isImage,
            }}
          />
        );
      })}
    </div>
  );
};

export default MessageFileAttachments;
